<script setup>
import { computed, ref, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import ContactReasonTreeNode from './ContactReasonTreeNode.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useOperatingProceduresStore } from 'dashboard/stores/operatingProcedures';
import OperatingProcedureRunsAPI from 'dashboard/api/operatingProcedureRuns';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  currentChat: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['cancel', 'update:show']);

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const inboxGetter = useMapGetter('inboxes/getInboxById');

const expandedReasons = ref(new Set());
const selectedReasons = ref(new Set());
const isLoading = ref(true);

// Reasons whose SOP run completed on this conversation are locked in — the
// completed run is an audit record of work done for that reason.
const operatingProceduresStore = useOperatingProceduresStore();
const completedRunProcedureIds = ref([]);

const lockedReasonIds = computed(() => {
  const sops = operatingProceduresStore.getRecords;
  return new Set(
    completedRunProcedureIds.value
      .map(id => sops.find(sop => sop.id === id)?.contact_reason_id)
      .filter(Boolean)
  );
});

const isLocked = reasonId => lockedReasonIds.value.has(reasonId);

const inbox = computed(() => {
  if (!props.currentChat?.inbox_id) return null;
  return useCamelCase(inboxGetter.value(props.currentChat.inbox_id), {
    deep: true,
  });
});

const filterReasonsByTargeting = reasons => {
  const currentMarket = inbox.value?.market;
  const currentInboxId = props.currentChat?.inbox_id;

  return reasons
    .filter(reason => {
      if (!reason.active) return false;

      const targetMarkets = reason.config?.targetMarkets || [];
      const targetInboxes = reason.config?.targetInboxes || [];

      const matchesMarket =
        targetMarkets.length === 0 ||
        (currentMarket && targetMarkets.includes(currentMarket));
      const matchesInbox =
        targetInboxes.length === 0 ||
        (currentInboxId && targetInboxes.includes(currentInboxId));

      return matchesMarket && matchesInbox;
    })
    .map(reason => {
      if (!reason.childReasons?.length) return reason;
      return {
        ...reason,
        childReasons: filterReasonsByTargeting(reason.childReasons),
      };
    });
};

const contactReasons = computed(() => {
  const reasons = useCamelCase(
    getters['contactReasons/getContactReasons'].value,
    {
      deep: true,
    }
  );

  return filterReasonsByTargeting(reasons);
});

const selectedReasonsList = computed(() => {
  return Array.from(selectedReasons.value)
    .map(id => {
      // Find reason by ID in the flat list
      const findReason = reasons => {
        for (const reason of reasons) {
          if (reason.id === id) return reason;
          if (reason.childReasons && reason.childReasons.length > 0) {
            const found = findReason(reason.childReasons);
            if (found) return found;
          }
        }
        return null;
      };
      return findReason(contactReasons.value);
    })
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));
});

const rootReasons = computed(() => {
  return contactReasons.value.filter(reason => !reason.parentId);
});

const localShow = computed({
  get() {
    return props.show;
  },
  set(value) {
    emit('update:show', value);
  },
});

const toggleExpand = reasonId => {
  if (expandedReasons.value.has(reasonId)) {
    expandedReasons.value.delete(reasonId);
  } else {
    expandedReasons.value.add(reasonId);
  }
};

const isExpanded = reasonId => {
  return expandedReasons.value.has(reasonId);
};

const toggleSelection = reasonId => {
  if (selectedReasons.value.has(reasonId)) {
    if (isLocked(reasonId)) return;
    selectedReasons.value.delete(reasonId);
  } else {
    selectedReasons.value.add(reasonId);
  }
};

const isSelected = reasonId => {
  return selectedReasons.value.has(reasonId);
};

const removeSelection = reasonId => {
  if (isLocked(reasonId)) return;
  selectedReasons.value.delete(reasonId);
};

const onCancel = () => {
  emit('cancel');
};

const onSubmit = async () => {
  try {
    await store.dispatch('updateContactReasons', {
      conversationId: props.currentChat.id,
      contactReasonIds: Array.from(selectedReasons.value),
    });
    useAlert(t('CONTACT_REASON_MODAL.UPDATE.SUCCESS_MESSAGE'));
    emitter.emit(BUS_EVENTS.CONTACT_REASONS_UPDATED, {
      conversationId: props.currentChat.id,
    });
    emit('cancel');
  } catch (error) {
    useAlert(t('CONTACT_REASON_MODAL.UPDATE.ERROR_MESSAGE'));
  }
};

onBeforeMount(async () => {
  try {
    isLoading.value = true;

    const existingReasons = getters['contactReasons/getContactReasons'].value;
    if (!existingReasons || existingReasons.length === 0) {
      await store.dispatch('contactReasons/get');
    }

    if (props.currentChat?.contact_reason_ids) {
      selectedReasons.value = new Set(props.currentChat.contact_reason_ids);
    }

    operatingProceduresStore.get();
    const { data } = await OperatingProcedureRunsAPI.forConversation({
      conversationId: props.currentChat.id,
    });
    completedRunProcedureIds.value = (data.payload || [])
      .filter(run => run.status === 'completed')
      .map(run => run.operating_procedure_id);
  } catch (error) {
    // Locking is best-effort in the UI; the backend still enforces it.
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onCancel" size="!w-[50%]">
    <div class="flex flex-col h-auto">
      <woot-modal-header
        :header-title="$t('CONTACT_REASON_MODAL.TITLE')"
        :header-content="$t('CONTACT_REASON_MODAL.DESC')"
      />
      <form class="w-full p-6" @submit.prevent="onSubmit">
        <div class="mb-6">
          <div v-if="isLoading" class="flex justify-center items-center py-12">
            <div class="flex flex-col items-center gap-3">
              <div
                class="w-8 h-8 border-4 border-slate-200 border-t-woot-500 rounded-full animate-spin"
              />
              <p class="text-sm text-slate-500 dark:text-slate-400">
                {{ $t('CONTACT_REASON_MODAL.LOADING') }}
              </p>
            </div>
          </div>
          <div v-else class="flex gap-4">
            <div class="flex-1 max-h-[600px] overflow-y-auto">
              <div
                v-if="rootReasons.length === 0"
                class="px-4 py-8 text-sm text-center text-slate-500 dark:text-slate-400"
              >
                {{ $t('CONTACT_REASON_MODAL.FORM.NO_REASONS') }}
              </div>
              <div v-else class="py-1">
                <ContactReasonTreeNode
                  v-for="reason in rootReasons"
                  :key="reason.id"
                  :reason="reason"
                  :level="0"
                  :is-expanded="isExpanded(reason.id)"
                  :is-selected="isSelected(reason.id)"
                  :expanded-reasons="expandedReasons"
                  :selected-reasons="selectedReasons"
                  @toggle-expand="toggleExpand"
                  @toggle-selection="toggleSelection"
                />
              </div>
            </div>
            <div class="flex-1 max-h-[600px] overflow-y-auto">
              <div
                v-if="selectedReasonsList.length === 0"
                class="flex flex-col items-center justify-center h-full gap-3 py-12 text-center"
              >
                <Icon
                  icon="i-lucide-list-checks"
                  class="w-10 h-10 text-slate-300 dark:text-slate-600"
                />
                <p
                  class="text-sm font-medium text-slate-500 dark:text-slate-400"
                >
                  {{ $t('CONTACT_REASON_MODAL.FORM.EMPTY_PLACEHOLDER_TITLE') }}
                </p>
                <p
                  class="text-xs text-slate-400 dark:text-slate-500 max-w-[180px]"
                >
                  {{ $t('CONTACT_REASON_MODAL.FORM.EMPTY_PLACEHOLDER_DESC') }}
                </p>
              </div>
              <div v-else class="flex flex-wrap gap-2">
                <div
                  v-for="reason in selectedReasonsList"
                  :key="reason.id"
                  class="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all bg-slate-100 dark:bg-slate-700"
                >
                  <Icon
                    icon="i-lucide-tag"
                    class="w-3.5 h-3.5 flex-shrink-0"
                    :style="{ color: reason.config?.color }"
                  />
                  <div
                    v-tooltip.bottom="reason.description"
                    class="text-sm whitespace-nowrap text-slate-800 dark:text-slate-100"
                  >
                    {{ reason.name }}
                  </div>
                  <Icon
                    v-if="isLocked(reason.id)"
                    v-tooltip.bottom="
                      $t('CONTACT_REASON_MODAL.FORM.LOCKED_BY_SOP')
                    "
                    icon="i-lucide-lock"
                    class="w-3.5 h-3.5 text-slate-400 dark:text-slate-500"
                  />
                  <Icon
                    v-else
                    icon="i-lucide-x"
                    class="w-4 h-4 cursor-pointer text-slate-500 dark:text-slate-400"
                    @click="removeSelection(reason.id)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-end gap-2 pt-4">
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            @click="onCancel"
          >
            {{ $t('CONTACT_REASON_MODAL.FORM.CANCEL') }}
          </button>
          <button
            type="submit"
            class="px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg transition-colors"
          >
            {{ $t('CONTACT_REASON_MODAL.FORM.SUBMIT') }}
          </button>
        </div>
      </form>
    </div>
  </woot-modal>
</template>
